import React, { useEffect, useContext, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { AuthContext } from '@/App';

const STORAGE_KEY = 'heh_onboarding_completed_v1';

/**
 * OnboardingTour, lightweight 5-step first-time tour for new members.
 * Fires automatically once per user (persisted in localStorage) when they
 * land on the dashboard. Admins and employees are skipped.
 *
 * Trigger a manual replay from anywhere with:
 *   window.dispatchEvent(new CustomEvent('heh:start-tour'))
 */
const OnboardingTour = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();

  const startTour = useCallback(() => {
    const d = driver({
      showProgress: true,
      progressText: 'Step {{current}} of {{total}}',
      nextBtnText: 'Next →',
      prevBtnText: '← Back',
      doneBtnText: 'Got it!',
      allowClose: true,
      overlayColor: '#4c0519',
      overlayOpacity: 0.6,
      stagePadding: 6,
      stageRadius: 8,
      popoverClass: 'heh-tour-popover',
      steps: [
        {
          element: '[data-testid="nav-logo"]',
          popover: {
            title: 'Welcome to Her Enterprise Hub! 👋',
            description:
              "You're in! This is your home base. Let us give you a quick 60-second tour so you know what everything does. You can skip anytime.",
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '[data-testid="nav-profile"]',
          popover: {
            title: '1. Complete your profile',
            description:
              'A complete profile, photo, bio, business info, socials, gets you far more visibility in the Members Directory. Aim for 80%+ completeness.',
            side: 'bottom',
            align: 'end',
          },
        },
        {
          element: '[data-testid="nav-members"]',
          popover: {
            title: '2. Explore the community',
            description:
              'The Members Directory lets you browse every woman in the community. Search by industry, location, or business name. Send a message to say hello.',
            side: 'bottom',
          },
        },
        {
          element: '[data-testid="nav-events"]',
          popover: {
            title: '3. Join an event',
            description:
              "Events are how you build real relationships. Free members can attend free events; Premium members can also host their own. RSVP and we'll email you a reminder.",
            side: 'bottom',
          },
        },
        {
          element: '[data-testid="nav-forum"]',
          popover: {
            title: '4. Post in the Forum',
            description:
              'Have a question, a win, or a challenge? Post it. The community is generous and responsive, introduce yourself in the "Introductions" category first.',
            side: 'bottom',
          },
        },
        {
          element: '[data-testid="nav-membership"]',
          popover: {
            title: '5. Explore Membership',
            description:
              'Free covers the essentials. Premium unlocks hosting events, running ads, priority support, and exclusive masterclasses. Upgrade any time.',
            side: 'bottom',
          },
        },
        {
          popover: {
            title: "You're ready to fly 🚀",
            description:
              'That\'s it, you know the shape of the place. Anything unclear? Visit the User Guide from the footer any time, or replay this tour from your dashboard.',
          },
        },
      ],
      onDestroyed: () => {
        localStorage.setItem(STORAGE_KEY, new Date().toISOString());
      },
    });
    d.drive();
  }, []);

  // Auto-trigger on first visit for regular members only,
  // OR force-trigger if ?tour=1 is in the URL (used by "Replay tour" links).
  useEffect(() => {
    if (!user) return;
    if (user.role === 'admin' || user.role === 'employee') return;

    const params = new URLSearchParams(location.search);
    const forceTour = params.get('tour') === '1';

    if (!forceTour && localStorage.getItem(STORAGE_KEY)) return;

    // Small delay so the nav and dashboard have fully rendered
    const timer = setTimeout(() => startTour(), 900);
    return () => clearTimeout(timer);
  }, [user, startTour, location.search]);

  // Listen for manual replay events
  useEffect(() => {
    const handler = () => startTour();
    window.addEventListener('heh:start-tour', handler);
    return () => window.removeEventListener('heh:start-tour', handler);
  }, [startTour]);

  return null; // Renders nothing directly
};

export default OnboardingTour;
